import React, { useEffect, useState, useRef } from "react";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import "./HeroSlider.css";

type Slide = {
  id: number;
  title: string;
  author: string;
  date: string;
  image: string;
};

const HERO_SLIDER_POSTS: Slide[] = [
  {
    id: 7,
    title: "15 Unheard Ways To Achieve Greater Walker",
    author: "Kirub",
    date: "Oct 16, 2025",
    image: "http://168.231.103.17:4500/wp-content/uploads/2025/11/Hero-Slide-1-1024x683.jpg",
  },
  {
    id: 13,
    title: "Your Light is About To Stop Being Relevant",
    author: "Kirub",
    date: "Oct 16, 2025",
    image: "http://168.231.103.17:4500/wp-content/uploads/2025/11/Hero-Slide-2-1024x685.jpg",
  },
];

const HeroSlider: React.FC = () => {
  const [slides] = useState<Slide[]>(HERO_SLIDER_POSTS);
  const [activeIndex, setActiveIndex] = useState(0);
  const intervalRef = useRef<number | null>(null);
  const isHoveringRef = useRef(false);
  const navigate = useNavigate();

  const stopAutoPlay = () => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const startAutoPlay = () => {
    stopAutoPlay();
    if (isHoveringRef.current) return;
    intervalRef.current = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
    }, 5000);
  };

  useEffect(() => {
    startAutoPlay();
    return stopAutoPlay;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Preload images
  useEffect(() => {
    slides.forEach((s) => {
      const img = new Image();
      img.src = s.image;
    });
  }, [slides]);

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev - 1 + slides.length) % slides.length);
    startAutoPlay();
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev + 1) % slides.length);
    startAutoPlay();
  };

  const goTo = (i: number) => {
    setActiveIndex(i);
    startAutoPlay();
  };

  const handleSlideClick = (id: number) => {
    navigate(`/post/${id}`);
  };

  const handleMouseEnter = () => {
    isHoveringRef.current = true;
    stopAutoPlay();
  };

  const handleMouseLeave = () => {
    isHoveringRef.current = false;
    startAutoPlay();
  };

  return (
    <div
      className="hero-slider-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="hero-slider">
        {/* Render ALL slides stacked; active one gets opacity 1, rest get 0 */}
        {slides.map((slide, i) => (
          <div
            key={slide.id}
            className={`hero-slide-wrapper ${i === activeIndex ? "slide-active" : "slide-inactive"}`}
            onClick={() => handleSlideClick(slide.id)}
            role="button"
            tabIndex={i === activeIndex ? 0 : -1}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSlideClick(slide.id);
            }}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="hero-slide-image"
              draggable={false}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <div className="hero-overlay" />
            <div className="hero-bottom-gradient" />

            <div className="hero-slide-text">
              <h2>{slide.title}</h2>
              <p>{slide.author} • {slide.date}</p>
            </div>
          </div>
        ))}

        <button
          className="nav-btn left"
          onClick={handlePrev}
          aria-label="Previous slide"
        >
          <LeftOutlined />
        </button>
        <button
          className="nav-btn right"
          onClick={handleNext}
          aria-label="Next slide"
        >
          <RightOutlined />
        </button>

        <div className="dots">
          {slides.map((s, i) => (
            <div
              key={s.id}
              className={`dot-wrapper ${i === activeIndex ? "dot-active" : "dot-inactive"}`}
              onClick={() => goTo(i)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter") goTo(i);
              }}
            >
              <div className="dot" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


export default HeroSlider;
